import type { FormEvent, KeyboardEvent } from 'react';
import { useI18n } from '../../../i18n/useI18n';
import { toastMutationError } from '../../../query/mutationToasts';
import { toast } from '../../../toast/toast';
import {
  CRAWL_DEFAULT_MAX_DEPTH,
  CRAWL_DEFAULT_MAX_PAGES,
  CRAWL_MAX_MAX_DEPTH,
  CRAWL_MAX_MAX_PAGES,
  CRAWL_MIN_MAX_DEPTH,
  CRAWL_MIN_MAX_PAGES,
  resolveCrawlLimitValue,
  sanitizeCrawlLimitInput,
} from '../crawlStartDefaults';
import { useStartKnowledgeCrawlMutation } from '../mutations/useKnowledgeCrawlMutations';
import { useKnowledgeCrawlsTabStore } from '../stores/useKnowledgeCrawlsTabStore';

export {
  CRAWL_DEFAULT_MAX_DEPTH,
  CRAWL_DEFAULT_MAX_PAGES,
  CRAWL_MIN_MAX_DEPTH,
  CRAWL_MAX_MAX_DEPTH,
  CRAWL_MIN_MAX_PAGES,
  CRAWL_MAX_MAX_PAGES,
  CRAWL_DEFAULT_USE_START_URL_AS_PREFIX_FILTER,
  CRAWL_DEFAULT_ALLOW_SUBDOMAINS,
} from '../crawlStartDefaults';

type UseCrawlStartFormOptions = {
  databaseId: string;
};

export function useCrawlStartForm({ databaseId }: UseCrawlStartFormOptions) {
  const { translate } = useI18n();
  const startCrawlMutation = useStartKnowledgeCrawlMutation();

  const crawlUrl = useKnowledgeCrawlsTabStore((state) => state.crawlUrl);
  const crawlMaxDepth = useKnowledgeCrawlsTabStore(
    (state) => state.crawlMaxDepth,
  );
  const crawlMaxPages = useKnowledgeCrawlsTabStore(
    (state) => state.crawlMaxPages,
  );
  const crawlUseStartUrlAsPrefixFilter = useKnowledgeCrawlsTabStore(
    (state) => state.crawlUseStartUrlAsPrefixFilter,
  );
  const crawlAllowSubdomains = useKnowledgeCrawlsTabStore(
    (state) => state.crawlAllowSubdomains,
  );
  const crawlExcludedUrls = useKnowledgeCrawlsTabStore(
    (state) => state.crawlExcludedUrls,
  );
  const setCrawlUrl = useKnowledgeCrawlsTabStore((state) => state.setCrawlUrl);
  const setCrawlMaxDepth = useKnowledgeCrawlsTabStore(
    (state) => state.setCrawlMaxDepth,
  );
  const setCrawlMaxPages = useKnowledgeCrawlsTabStore(
    (state) => state.setCrawlMaxPages,
  );
  const setCrawlUseStartUrlAsPrefixFilter = useKnowledgeCrawlsTabStore(
    (state) => state.setCrawlUseStartUrlAsPrefixFilter,
  );
  const setCrawlAllowSubdomains = useKnowledgeCrawlsTabStore(
    (state) => state.setCrawlAllowSubdomains,
  );
  const setCrawlExcludedUrls = useKnowledgeCrawlsTabStore(
    (state) => state.setCrawlExcludedUrls,
  );
  const resetCrawlForm = useKnowledgeCrawlsTabStore(
    (state) => state.resetCrawlForm,
  );

  const preventInvalidNumericKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (
      event.key === '-' ||
      event.key === '+' ||
      event.key === 'e' ||
      event.key === 'E' ||
      event.key === '.' ||
      event.key === ','
    ) {
      event.preventDefault();
    }
  };

  const handleCrawlMaxDepthChange = (value: string) => {
    setCrawlMaxDepth(
      sanitizeCrawlLimitInput(value, CRAWL_MIN_MAX_DEPTH, CRAWL_MAX_MAX_DEPTH),
    );
  };

  const handleCrawlMaxPagesChange = (value: string) => {
    setCrawlMaxPages(
      sanitizeCrawlLimitInput(value, CRAWL_MIN_MAX_PAGES, CRAWL_MAX_MAX_PAGES),
    );
  };

  const handleCrawlMaxDepthBlur = () => {
    setCrawlMaxDepth(
      String(
        resolveCrawlLimitValue(
          crawlMaxDepth,
          CRAWL_MIN_MAX_DEPTH,
          CRAWL_MAX_MAX_DEPTH,
          CRAWL_DEFAULT_MAX_DEPTH,
        ),
      ),
    );
  };

  const handleCrawlMaxPagesBlur = () => {
    setCrawlMaxPages(
      String(
        resolveCrawlLimitValue(
          crawlMaxPages,
          CRAWL_MIN_MAX_PAGES,
          CRAWL_MAX_MAX_PAGES,
          CRAWL_DEFAULT_MAX_PAGES,
        ),
      ),
    );
  };

  const handleStartCrawl = (
    event: FormEvent<HTMLFormElement>,
    options?: { onSuccess?: () => void },
  ) => {
    event.preventDefault();

    const maxDepth = resolveCrawlLimitValue(
      crawlMaxDepth,
      CRAWL_MIN_MAX_DEPTH,
      CRAWL_MAX_MAX_DEPTH,
      CRAWL_DEFAULT_MAX_DEPTH,
    );
    const maxPages = resolveCrawlLimitValue(
      crawlMaxPages,
      CRAWL_MIN_MAX_PAGES,
      CRAWL_MAX_MAX_PAGES,
      CRAWL_DEFAULT_MAX_PAGES,
    );

    setCrawlMaxDepth(String(maxDepth));
    setCrawlMaxPages(String(maxPages));

    startCrawlMutation.mutate(
      {
        databaseId,
        url: crawlUrl.trim(),
        options: {
          maxDepth,
          maxPages,
          useStartUrlAsPrefixFilter: crawlUseStartUrlAsPrefixFilter,
          allowSubdomains: crawlUseStartUrlAsPrefixFilter
            ? false
            : crawlAllowSubdomains,
          excludedUrls: crawlExcludedUrls,
        },
      },
      {
        onSuccess: () => {
          toast.success({
            title: translate('knowledge.crawlStartedTitle'),
            description: '',
          });
          resetCrawlForm();
          options?.onSuccess?.();
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('knowledge.crawlErrorTitle'),
          );
        },
      },
    );
  };

  return {
    crawlUrl,
    crawlMaxDepth,
    crawlMaxPages,
    crawlUseStartUrlAsPrefixFilter,
    crawlAllowSubdomains,
    crawlExcludedUrls,
    setCrawlUrl,
    setCrawlMaxDepth: handleCrawlMaxDepthChange,
    setCrawlMaxPages: handleCrawlMaxPagesChange,
    onCrawlMaxDepthBlur: handleCrawlMaxDepthBlur,
    onCrawlMaxPagesBlur: handleCrawlMaxPagesBlur,
    setCrawlUseStartUrlAsPrefixFilter,
    setCrawlAllowSubdomains,
    setCrawlExcludedUrls,
    resetCrawlForm,
    handleStartCrawl,
    preventInvalidNumericKey,
    isStartingCrawl: startCrawlMutation.isPending,
  };
}
